/**
 * Centralized query key factory for TanStack Query
 * Provides consistent, hierarchical query keys across the application
 */

export const queryKeys = {
  // Contacts
  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (workspaceId?: string, filters?: Record<string, any>) =>
      [...queryKeys.contacts.lists(), { workspaceId, filters }] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
    search: (workspaceId?: string, query?: string) =>
      [...queryKeys.contacts.all, 'search', { workspaceId, query }] as const,
    options: (workspaceId?: string) =>
      [...queryKeys.contacts.all, 'options', workspaceId] as const,
    deleted: (workspaceId?: string) =>
      [...queryKeys.contacts.all, 'deleted', { workspaceId }] as const,
  },

  // Workspaces
  workspaces: {
    all: ['workspaces'] as const,
    lists: () => [...queryKeys.workspaces.all, 'list'] as const,
    list: (userId?: string) => 
      [...queryKeys.workspaces.lists(), { userId }] as const,
    details: () => [...queryKeys.workspaces.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workspaces.details(), id] as const,
    settings: (id: string) => 
      [...queryKeys.workspaces.detail(id), 'settings'] as const,
    members: (id: string) => 
      [...queryKeys.workspaces.detail(id), 'members'] as const,
  },

  // Calendar
  calendarCalendars: {
    all: ['calendarCalendars'] as const,
    lists: () => [...queryKeys.calendarCalendars.all, 'list'] as const,
    list: (workspaceId?: string, providerId?: string) =>
      [...queryKeys.calendarCalendars.lists(), { workspaceId, providerId }] as const,
  },

  calendarEvents: {
    all: ['calendarEvents'] as const,
    lists: () => [...queryKeys.calendarEvents.all, 'list'] as const,
    list: (workspaceId?: string, dateRange?: { start: string; end: string }) =>
      [...queryKeys.calendarEvents.lists(), { workspaceId, dateRange }] as const,
    details: () => [...queryKeys.calendarEvents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.calendarEvents.details(), id] as const,
  },

  // Views/Filters
  views: {
    all: ['views'] as const,
    lists: () => [...queryKeys.views.all, 'list'] as const,
    list: (workspaceId?: string) => 
      [...queryKeys.views.lists(), { workspaceId }] as const,
    details: () => [...queryKeys.views.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.views.details(), id] as const,
  },

  // Email Settings
  emailSettings: {
    all: ['emailSettings'] as const,
    senders: (workspaceId?: string) => 
      [...queryKeys.emailSettings.all, 'senders', workspaceId] as const,
    providers: (workspaceId?: string) => 
      [...queryKeys.emailSettings.all, 'providers', workspaceId] as const,
  },

  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Duplicates
  duplicates: {
    all: ['duplicates'] as const,
    lists: () => [...queryKeys.duplicates.all, 'list'] as const,
    list: (workspaceId?: string) => 
      [...queryKeys.duplicates.lists(), { workspaceId }] as const,
  },

  // Import History
  importHistory: {
    all: ['importHistory'] as const,
    lists: () => [...queryKeys.importHistory.all, 'list'] as const,
    list: (workspaceId?: string) =>
      [...queryKeys.importHistory.lists(), { workspaceId }] as const,
  },

  // Help Center
  helpCenter: {
    all: ['helpCenter'] as const,
    categories: () => [...queryKeys.helpCenter.all, 'categories'] as const,
    articles: (categorySlug?: string) => [...queryKeys.helpCenter.all, 'articles', categorySlug] as const,
    article: (categorySlug: string, articleSlug: string) => [...queryKeys.helpCenter.all, 'article', categorySlug, articleSlug] as const,
    search: (query: string) => [...queryKeys.helpCenter.all, 'search', query] as const,
  },

  // System Roles
  systemRoles: {
    all: ['systemRoles'] as const,
    userRoles: (userId: string) => [...queryKeys.systemRoles.all, 'user', userId] as const,
  },

  // Custom Fields
  customFields: {
    all: ['customFields'] as const,
    lists: () => [...queryKeys.customFields.all, 'list'] as const,
    list: (workspaceId?: string) =>
      [...queryKeys.customFields.lists(), { workspaceId }] as const,
  },

  // API Activity
  apiActivity: {
    all: ['apiActivity'] as const,
    lists: () => [...queryKeys.apiActivity.all, 'list'] as const,
    list: (workspaceId?: string) =>
      [...queryKeys.apiActivity.lists(), { workspaceId }] as const,
  },

  // Groups
  groups: {
    all: ['groups'] as const,
    lists: () => [...queryKeys.groups.all, 'list'] as const,
    list: (workspaceId?: string) =>
      [...queryKeys.groups.lists(), { workspaceId }] as const,
    nav: (workspaceId?: string) =>
      [...queryKeys.groups.all, 'nav', { workspaceId }] as const,
    details: () => [...queryKeys.groups.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.groups.details(), id] as const,
  },

  // Activities
  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (workspaceId?: string, view?: string, page?: number) =>
      [...queryKeys.activities.lists(), { workspaceId, view, page }] as const,
    contact: (contactId: string, workspaceId?: string) =>
      [...queryKeys.activities.all, 'contact', { contactId, workspaceId }] as const,
    counts: (workspaceId?: string) =>
      [...queryKeys.activities.all, 'counts', workspaceId] as const,
    // Oldest outstanding (scheduled, not completed) activity per contact, for a batch of
    // visible contact ids — powers the contact-list "Event" line.
    oldestOutstandingBatch: (workspaceId?: string, contactIds?: string[]) =>
      [...queryKeys.activities.all, 'oldest-outstanding-batch', workspaceId, contactIds] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    kpis: (workspaceId: string, fromIso: string, toIso: string, userFilter: string | null) =>
      [...queryKeys.analytics.all, 'kpis', { workspaceId, fromIso, toIso, userFilter }] as const,
    series: (workspaceId: string, metric: string, fromIso: string, toIso: string, granularity: string, userFilter: string | null, extra?: Record<string, unknown>) =>
      [...queryKeys.analytics.all, 'series', metric, { workspaceId, fromIso, toIso, granularity, userFilter, ...extra }] as const,
    leaderboard: (workspaceId: string, fromIso: string, toIso: string, sortBy: string, role: string) =>
      [...queryKeys.analytics.all, 'leaderboard', { workspaceId, fromIso, toIso, sortBy, role }] as const,
    drilldown: (workspaceId: string, metric: string, fromIso: string, toIso: string, userFilter: string | null, segment: string | null) =>
      [...queryKeys.analytics.all, 'drilldown', metric, { workspaceId, fromIso, toIso, userFilter, segment }] as const,
  },

  // Enterprise SSO partners (admin surface)
  enterprisePartners: {
    all: ['enterprisePartners'] as const,
    list: () => [...queryKeys.enterprisePartners.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.enterprisePartners.all, 'detail', id] as const,
    admins: (id: string) => [...queryKeys.enterprisePartners.all, 'admins', id] as const,
    identities: (id: string) => [...queryKeys.enterprisePartners.all, 'identities', id] as const,
    auditLog: (id: string) => [...queryKeys.enterprisePartners.all, 'auditLog', id] as const,
    myAdmin: (userId: string) => [...queryKeys.enterprisePartners.all, 'myAdmin', userId] as const,
    myCapabilities: (userId: string) =>
      [...queryKeys.enterprisePartners.all, 'myCapabilities', userId] as const,
    capabilityList: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'capabilityList', partnerId] as const,
    emulationMembers: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'emulationMembers', partnerId] as const,
    monitoringCampaigns: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'monitoringCampaigns', partnerId] as const,
    senderHealth: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'senderHealth', partnerId] as const,
    memberQuota: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'memberQuota', partnerId] as const,
    memberAnalytics: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'memberAnalytics', partnerId] as const,
    emulationActivity: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'emulationActivity', partnerId] as const,
    seatUsage: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'seatUsage', partnerId] as const,
    memberList: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'memberList', partnerId] as const,
    workspaceSeatBreakdown: (partnerId: string) =>
      [...queryKeys.enterprisePartners.all, 'workspaceSeatBreakdown', partnerId] as const,
    campaignFailures: (campaignId: string) =>
      [...queryKeys.enterprisePartners.all, 'campaignFailures', campaignId] as const,
  },

  // Legal consents (per-user)
  consents: {
    all: ['consents'] as const,
    pending: (userId?: string) =>
      [...queryKeys.consents.all, 'pending', userId] as const,
  },

  // In-app notifications (workspace-scoped, per-user)
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (workspaceId?: string, userId?: string) =>
      [...queryKeys.notifications.lists(), { workspaceId, userId }] as const,
    unreadCount: (workspaceId?: string, userId?: string) =>
      [...queryKeys.notifications.all, 'unreadCount', { workspaceId, userId }] as const,
  },
} as const;

/**
 * Helper function to create mutation keys
 */
export const mutationKeys = {
  // Contacts
  createContact: ['contacts', 'create'] as const,
  updateContact: ['contacts', 'update'] as const,
  deleteContact: ['contacts', 'delete'] as const,
  mergeContacts: ['contacts', 'merge'] as const,
  restoreContacts: ['contacts', 'restore'] as const,

  // Workspaces
  createWorkspace: ['workspaces', 'create'] as const,
  updateWorkspace: ['workspaces', 'update'] as const,
  deleteWorkspace: ['workspaces', 'delete'] as const,

  // Campaigns
  createCampaign: ['campaigns', 'create'] as const,
  updateCampaign: ['campaigns', 'update'] as const,
  deleteCampaign: ['campaigns', 'delete'] as const,
  scheduleCampaign: ['campaigns', 'schedule'] as const,

  // Calendar
  createEvent: ['calendarEvents', 'create'] as const,
  updateEvent: ['calendarEvents', 'update'] as const,
  deleteEvent: ['calendarEvents', 'delete'] as const,
  syncCalendar: ['calendar', 'sync'] as const,
  toggleCalendar: ['calendarCalendars', 'toggle'] as const,

  // Bulk Emails
  createBulkEmail: ['bulkEmails', 'create'] as const,
  updateBulkEmail: ['bulkEmails', 'update'] as const,
  deleteBulkEmail: ['bulkEmails', 'delete'] as const,
  sendBulkEmail: ['bulkEmails', 'send'] as const,

  // Imports
  importContacts: ['imports', 'create'] as const,
  undoImport: ['imports', 'delete'] as const,

  // Groups
  createGroup: ['groups', 'create'] as const,
  updateGroup: ['groups', 'update'] as const,
  deleteGroup: ['groups', 'delete'] as const,
  addToGroup: ['groups', 'addContacts'] as const,
  removeFromGroup: ['groups', 'removeContacts'] as const,
} as const;

/**
 * Helper function to invalidate related queries after mutations
 */
export const getInvalidationKeys = {
  afterContactMutation: (workspaceId?: string) => [
    queryKeys.contacts.lists(),
    queryKeys.contacts.list(workspaceId),
    queryKeys.duplicates.list(workspaceId),
  ],

  afterWorkspaceMutation: (userId?: string) => [
    queryKeys.workspaces.lists(),
    queryKeys.workspaces.list(userId),
  ],

  afterCalendarEventMutation: (workspaceId?: string) => [
    queryKeys.calendarEvents.lists(),
    queryKeys.calendarEvents.list(workspaceId),
  ],

  afterGroupMutation: (workspaceId?: string) => [
    queryKeys.groups.lists(),
    queryKeys.groups.list(workspaceId),
    queryKeys.groups.nav(workspaceId),
  ],
} as const;